import { ChakraFlex, InputGroupV2, UniversalSelect } from '@equip.health/ui';
import { getConsentText, getConsentToRecordOptions } from '../../../util';
import { ConsentToRecord } from '~/lib/constants/patients.constants';

import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { toTagsUniversalOption } from '~/lib/util/menu';
import {
  getFormattedOptions,
  patientEnumMenuOptionsMapper,
  handleDisableOption,
} from '~/lib/util/patient.util';
import { getMultiSelectedUniversalSelectOptions } from '~/lib/util/profile.util';
import {
  useGetEthnicitiesEnumQuery,
  useGetGendersEnumQuery,
  useGetPatientTagsEnumQuery,
  useGetProviderLanguagesEnumQuery,
  useGetProviderPronounsEnumQuery,
  useGetSexesEnumQuery,
} from '~/store/services/enumerations';

interface ProfileDemographicsProps {
  appendSurveyResult: any;
  data: ProfileDemographicsData;
  updateSection: (
    section: 'demographics',
    updateDeata: ProfileDemographicsData,
  ) => void;
}

const ProfileDemographics = ({
  appendSurveyResult,
  data,
  updateSection,
}: ProfileDemographicsProps) => {
  const TOP_LEVEL_ID = 'profile-demographics';

  const { data: sexes, isLoading: isGetSexesLoading } = useGetSexesEnumQuery();
  const sexesOptions = getFormattedOptions(sexes);
  const { data: genders, isLoading: isGetGendersLoading } =
    useGetGendersEnumQuery();
  const gendersOptions = patientEnumMenuOptionsMapper(genders);
  const { data: pronouns, isLoading: isGetPronounsLoading } =
    useGetProviderPronounsEnumQuery();

  const pronounsOptions = patientEnumMenuOptionsMapper(pronouns);
  const { data: ethnicities, isLoading: isGetEthnicitiesLoading } =
    useGetEthnicitiesEnumQuery();
  const ethnicitiesOptions = patientEnumMenuOptionsMapper(ethnicities);
  const { data: languages, isLoading: isGetLanguagesLoading } =
    useGetProviderLanguagesEnumQuery();
  const languagesOptions = patientEnumMenuOptionsMapper(languages);
  const { data: tagValues, isLoading: isGetPatientTagsEnumLoading } =
    useGetPatientTagsEnumQuery();
  const tagOptions: UniversalSelectOption[] = tagValues
    ? tagValues.map(toTagsUniversalOption)
    : [];

  const handleFieldChange = (
    field: keyof ProfileDemographicsData,
    value: string | string[] | boolean | null,
  ) => {
    const fieldValue =
      value === undefined || (typeof value === 'string' && value === '')
        ? null
        : value;
    const updatedData = { ...data, [field]: fieldValue };
    updateSection('demographics', updatedData);
  };

  const shouldHighlightField = (fieldName: string): boolean => {
    return appendSurveyResult.hasOwnProperty(fieldName);
  };

  return (
    <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__sex`}
        isHighlight={shouldHighlightField('sex')}
        label="Sex assigned at birth"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__sex-select`}
          isHighlight={shouldHighlightField('sex')}
          isLoading={isGetSexesLoading}
          isSingleSelect
          onChange={(option) => handleFieldChange('sex', option?.value)}
          options={sexesOptions}
          value={getSelectedUniversalSelectOption(data.sex, sexesOptions)}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__gender-on-record`}
        isHighlight={shouldHighlightField('genderOnRecord')}
        label="Gender on record"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__gender-on-record-select`}
          isHighlight={shouldHighlightField('genderOnRecord')}
          isLoading={isGetGendersLoading}
          isSingleSelect
          onChange={(option) =>
            handleFieldChange('genderOnRecord', option?.value)
          }
          options={gendersOptions}
          value={getSelectedUniversalSelectOption(
            data.genderOnRecord,
            gendersOptions,
          )}
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__pronouns-on-record`}
        isHighlight={shouldHighlightField('pronounsOnRecord')}
        label="Pronouns on record"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__pronouns-on-record-select`}
          isHighlight={shouldHighlightField('pronounsOnRecord')}
          isLoading={isGetPronounsLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('pronounsOnRecord', selectedValues);
          }}
          options={pronounsOptions}
          value={getMultiSelectedUniversalSelectOptions(
            data.pronounsOnRecord,
            pronounsOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__gender-off-record`}
        label="Gender off record"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__gender-off-record-select`}
          isLoading={isGetGendersLoading}
          isSingleSelect
          onChange={(option) =>
            handleFieldChange('genderOffRecord', option?.value)
          }
          options={gendersOptions}
          value={getSelectedUniversalSelectOption(
            data.genderOffRecord,
            gendersOptions,
          )}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__pronouns-off-record`}
        isHighlight={shouldHighlightField('pronounsOffRecord')}
        label="Pronouns off record"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__pronouns-off-record-select`}
          isHighlight={shouldHighlightField('pronounsOffRecord')}
          isLoading={isGetPronounsLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('pronounsOffRecord', selectedValues);
          }}
          options={pronounsOptions}
          value={getMultiSelectedUniversalSelectOptions(
            data.pronounsOffRecord,
            pronounsOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__ethnicity`}
        isHighlight={shouldHighlightField('ethnicities')}
        label="Race/Ethnicity"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__ethnicity-select`}
          isHighlight={shouldHighlightField('ethnicities')}
          isLoading={isGetEthnicitiesLoading}
          isOptionDisabled={handleDisableOption}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('ethinicities', selectedValues);
          }}
          options={ethnicitiesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            data.ethinicities,
            ethnicitiesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__languages-spoken`}
        label="Language(s) spoken"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__languages-spoken-select`}
          isHighlight={shouldHighlightField('languages')}
          isLoading={isGetLanguagesLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('languagesSpoken', selectedValues);
          }}
          options={languagesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            data.languagesSpoken,
            languagesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
      <InputGroupV2 id={`${TOP_LEVEL_ID}__patient-tags`} label="Tags">
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__patient-tags-select`}
          isLoading={isGetPatientTagsEnumLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('patientTags', selectedValues);
          }}
          options={tagOptions}
          value={getMultiSelectedUniversalSelectOptions(
            data.patientTags,
            tagOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>

      <InputGroupV2
        id={`${TOP_LEVEL_ID}__patient-consent-to-record`}
        label="Consent to record"
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__patient-consent-to-record-select`}
          isClearable={false}
          isSingleSelect
          onChange={(options) => {
            const consentGiven = options.value == ConsentToRecord.ConsentGiven;
            handleFieldChange('hasGivenRecordingConsent', consentGiven);
          }}
          options={getConsentToRecordOptions(TOP_LEVEL_ID)}
          placeholder="Please choose"
          value={getSelectedUniversalSelectOption(
            getConsentText(data.hasGivenRecordingConsent),
            getConsentToRecordOptions(TOP_LEVEL_ID),
          )}
        />
      </InputGroupV2>
    </ChakraFlex>
  );
};

export default ProfileDemographics;
